import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { PasswordResetSendCodeDialogComponent } from "../../../auth/password-reset-send-code-dialog/password-reset-send-code-dialog.component";
import { ExistingUserActionSelectionDialogComponent } from "../../../auth/existing-user-action-selection-dialog/existing-user-action-selection-dialog.component";
import { ResetPasswordDialogComponent } from "../../../auth/reset-password-dialog/reset-password-dialog.component";
import { AuthenticationRequest } from "../../../models/auth/auth-request.model";
import { LoginStatus } from "../../../models/auth/login-status.enum";
import { ResetPasswordResult } from "../../../models/auth/reset-password-result.model";
import { CreateBorrowerAccountMortgageRequest } from "../../../models/create-borrower-account-mortgage-request.model";
import { ErrorMessage } from "../../../models/error-message.model";
import { ExistingAccountLoginResult } from "../../../models/existing-account-login-result.model";
import { CreateAccountStep } from "../../../models/wizard/create-account-step.model";
import { CreateAccountStepConfig } from "projects/borrower-app/src/app/models/wizard/config/create-account-step-config.model";
import { LocalizedEditService } from "projects/borrower-app/src/app/transloco/localized-edit.service";
import { AuthService } from "../../../services/auth.service";
import { NavigationService } from "../../../services/navigation.service";
import { WizardFlowConfigServiceBase } from "../../../services/wizard/wizard-flow-config-service.base";
import { WizardFlowServiceBase } from "../../../services/wizard/wizard-flow-service.base";
import { BorrowerInfoComponent } from "../../borrower-info/borrower-info.component";
import { WizardStepComponentBase } from "../wizard-step-base.component";
import { LegalDocumentsService } from "projects/borrower-app/src/app/services/legal-documents.service";
import { loadLegalDocumentOrReport } from "projects/borrower-app/src/app/services/legal-documents";
import { HtmlViewerDialogComponent } from "projects/shared/html-viewer-dialog/html-viewer-dialog.component";
import { Constants } from "../../../services/constants";
import { Subscription } from "rxjs";
import { MortgageApplicationService } from "../../../services/mortgage-application.service";
import { CompanyInfo } from "../../../models/company-info.model";
import { EnvironmentService } from "projects/shared/services/environment.service";
import { ActivatedRoute } from "@angular/router";
import { Utils } from "../../../services/utils";

@Component({
    selector: 'create-account-step',
    templateUrl: 'create-account-step.component.html',
    styleUrls: ['create-account-step.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAccountStepComponent extends WizardStepComponentBase<CreateAccountStep, CreateAccountStepConfig> implements OnInit, OnDestroy {

  @ViewChild(BorrowerInfoComponent) borrowerInfo: BorrowerInfoComponent | undefined;

  error: any | undefined = undefined;
  userExists: boolean = false;

passwordPolicyViolations: { id: string; message: string }[] | null = null;

  isSubmittedInEditMode: boolean = false;

  protected companyInfo: CompanyInfo = null;

  protected companyName: string = "Lodasoft";

  protected optInForReceivingSms: boolean = false;

  // Company 263 gets a "Reply HELP" sentence after the default SMS consent text.
  protected showReplyHelpDisclaimer: boolean = false;

  private _activatedRouteQueryParamsSubscription: Subscription;
  private _activatedRouteParamsSubscription: Subscription;

  private _queryParams: string = "";

  // The borrower-facing account form. A company with external auth never has its borrowers fill
  // this in - they are challenged by their own IdP instead - but the flow editor renders this
  // component for real, so the form (and the authoring controls inside it) must still show there
  // or the step cannot be edited at all.
  protected get showAccountForm(): boolean {
    return !!this.companyInfo && (!this.companyInfo.externalAuthEnabled || this.isEditEnabled);
  }

  // True exactly when this component hands the browser off to the company's IdP: the placeholder
  // below and the redirect itself are driven from this one flag so they can never disagree. Never
  // true in the flow editor - there is no borrower to challenge and nothing to wait for.
  protected get isExternalAuthHandoff(): boolean {
    return !!this.companyInfo && this.companyInfo.externalAuthEnabled && !this.isEditEnabled;
  }

  // The flow's custom SMS consent HTML for the active language, or undefined to show the default.
  // Like the localizedLabel pipe, the base value only applies to the default language, so an
  // English customization never replaces the default Spanish text.
  protected get customSmsConsentHtml(): string | undefined {
    const lang = this.translocoService.getActiveLang();
    const custom = this.config?._i18n?.[lang]?.['smsConsentText']
      || (lang === this.translocoService.getDefaultLang() ? this.config?.smsConsentText : undefined);
    return custom?.trim() ? custom : undefined;
  }

  get userCreationAutoConfirmed(): boolean {
    return this._wizardFlowConfigService.flowConfigs.userCreationAutoConfirmed;
  }

  set userCreationAutoConfirmed(value: boolean) {
    this._wizardFlowConfigService.flowConfigs.userCreationAutoConfirmed = value;
  }

  constructor(private readonly _injector: Injector,
    private readonly _wizardFlowService: WizardFlowServiceBase,
    private readonly _wizardFlowConfigService: WizardFlowConfigServiceBase,
    private readonly _navigationService: NavigationService,
    private readonly _modalService: NgbModal,
    private readonly _authService: AuthService,
    private readonly _mortgageApplicationService: MortgageApplicationService,
    private readonly _environment: EnvironmentService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _legalDocuments: LegalDocumentsService,
    public readonly i18n: LocalizedEditService) {
    super(_injector);
    if (this.wizardFlowService.context.hasSuccessfullyPulledExistingApplication
      && this.mortgageApplication && this.mortgageApplication.borrowers.length > 0) {
      this.step.borrower = this.mortgageApplication.borrowers[0];
    }
  }

  ngOnInit(): void {
    this._activatedRouteQueryParamsSubscription = this._activatedRoute.queryParams.subscribe((queryParams) => {
			this._activatedRouteParamsSubscription = this._activatedRoute.params.subscribe((routeParams) => {
				Object.keys(queryParams).forEach(key => {
					this._queryParams += `${key}=${queryParams[key]}&`;
				});
        this.getCompanyInfo();
			});
		});
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._activatedRouteQueryParamsSubscription?.unsubscribe();
    this._activatedRouteParamsSubscription?.unsubscribe();
  }

  onLoginClicked = () => {
    this._navigationService.navigateToLogin();
  }

  onNextClicked() {
    this.error = undefined;
    if (this.wizardFlowService.isEditEnabled) {
      this.isSubmittedInEditMode = true;
      this.cdr.detectChanges();
      return;
    }
    if (this.borrowerInfo?.validate()) {
      localStorage.setItem("borrowerIndex", "0");
      if (this._wizardFlowService.companyGuid) {
        this.startSpinner();
        this.cdr.detectChanges();

        this.mortgageApplicationService.checkIfBorrowerExists(this._wizardFlowService.companyGuid, this.borrowerInfo.borrower?.primaryEmail).subscribe(response => {
          this.stopSpinner();
          this.cdr.detectChanges();
          if (response.userExistsForCompany) {
            // User exists for this company, we need to ask them what user is intending to do - either create an app, or login and manage applications
            this.showRequestPasswordDialog(this.step.borrower!.primaryEmail);
          } else {
            if (!this.userCreationAutoConfirmed) {
              // If user does not exist, the backend sent the confirmation email, nothing to do here any further
              this.createUserAccountAndMortgageWhenNotAutoConfirmed()
            } else {
              // User does not exists, and it's auto-confirmed, so we need to send the userid/password and create the account and the app
              this.createUserAccountAndMortgageWhenAutoConfirmed();
            }
          }
        }, error => {
          this.stopSpinner();
          this.cdr.detectChanges();
        })
      }
    }
  }

  protected onShowPrivacyPolicyClicked = async () => {
    const privacyStatement = await this.loadLegalDocument(this.getPrivacyStatement);
    if (privacyStatement === null) {
      return;
    }
    const modalRef = this._modalService.open(HtmlViewerDialogComponent, Constants.modalOptions.xlarge);
    modalRef.componentInstance.title = this.translocoService.translate('wizard.createAccount.privacyPolicyTitle');
    modalRef.componentInstance.htmlContent = privacyStatement;

    modalRef.result.then(() => {
    }, error => { });
  }

  protected onShowTermsOfServiceClicked = async () => {
    const termsOfService = await this.loadLegalDocument(this.getTermsOfUse);
    if (termsOfService === null) {
      return;
    }
    const modalRef = this._modalService.open(HtmlViewerDialogComponent, Constants.modalOptions.xlarge);
    modalRef.componentInstance.title = this.translocoService.translate('wizard.createAccount.termsOfServiceTitle');
    modalRef.componentInstance.htmlContent = termsOfService;

    modalRef.result.then(() => {
    }, error => { });
  }

  private getCompanyInfo = () => {
    this._mortgageApplicationService.getCompanyInfo(this._wizardFlowService.companyGuid).subscribe(response => {
      this.companyInfo = response;
      this.companyName = this.companyInfo.companyName;
      this.showReplyHelpDisclaimer = this.companyInfo.companyId === 263;

      // The flow editor renders this component for real in its preview pane, so ngOnInit runs while
      // an admin is authoring the step. Navigating the editor tab to the company's identity
      // provider there would make the step impossible to edit - only a live borrower gets challenged.
      if (this.isExternalAuthHandoff) {
        this.redirectToExtternalAuthProvider();
      }
      this.cdr.detectChanges();
    }, error => {
      this.cdr.detectChanges();
    });
  }

  private redirectToExtternalAuthProvider = () => {
    const mortgageToSave: any = {
      subjectProperty: this.mortgageApplication.subjectProperty,
      extension: this.mortgageApplication.extension,
      userGuid: this.mortgageApplication.userGuid,
    }
    if (mortgageToSave.userGuid === 'unassigned') {
      mortgageToSave.userGuid = undefined;
    }
    const createApplicationRequest: CreateBorrowerAccountMortgageRequest = {
      companyGuid: this._wizardFlowService.companyGuid,
      branchGuid: this._wizardFlowService.branchGuid,
      campaignId: this._wizardFlowService.campaignId,
      leadId: this._wizardFlowService.leadId,
      leadGuid: this._wizardFlowService.leadGuid,
      leadSourceOverride: this._wizardFlowService.leadSourceOverride || this._wizardFlowService.context.leadSource,
      mortgage: mortgageToSave,
      userGuid: this.mortgageApplication.userGuid,
      referralSource: this._wizardFlowService.referralSource || this._wizardFlowService.context.referralAgentId || undefined,
      agentTypeId: this._wizardFlowService.context.agentTypeId || undefined,
      flowGuid: this._wizardFlowConfigService.flowConfigs.guid,
      loanPurposeId: this._wizardFlowService.context.lead?.loanPurposeId || this._wizardFlowService.context.loanPurposeId,
    }
    if (createApplicationRequest.userGuid === 'unassigned') {
      createApplicationRequest.userGuid = undefined;
    }

    const base64String = btoa(JSON.stringify(createApplicationRequest));
    const returnUrl = `${window.location.protocol}//${window.location.host}/create-account-ext-auth?s=${base64String}${this._queryParams ? '&' + this._queryParams : ''}`;
    const challengeUrl = this._environment.apiInfo.authApiBaseUrl + `/api/auth/connect/challenge/${this._wizardFlowService.companyGuid}/Borrower?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = challengeUrl;
  }

  private createUserAccountAndMortgageWhenAutoConfirmed = () => {
    const createApplicationRequest: CreateBorrowerAccountMortgageRequest = {
      companyGuid: this._wizardFlowService.companyGuid,
      branchGuid: this._wizardFlowService.branchGuid,
      password: this.borrowerInfo.password,
      campaignId: this._wizardFlowService.campaignId,
      leadId: this._wizardFlowService.leadId,
      leadGuid: this._wizardFlowService.leadGuid,
      leadSourceOverride: this._wizardFlowService.leadSourceOverride || this._wizardFlowService.context.leadSource,
      mortgage: this.mortgageApplication,
      userGuid: this.mortgageApplication.userGuid,
      referralSource: this._wizardFlowService.referralSource || this._wizardFlowService.context.referralAgentId || undefined,
      agentTypeId: this._wizardFlowService.context.agentTypeId || undefined,
      flowGuid: this._wizardFlowConfigService.flowConfigs.guid,
      loanPurposeId: this._wizardFlowService.context.lead?.loanPurposeId || this._wizardFlowService.context.loanPurposeId,
      smsOptIn: this.optInForReceivingSms
    }
    if (createApplicationRequest.userGuid === 'unassigned') {
      createApplicationRequest.userGuid = undefined;
    }
    this.startSpinner();
    this.cdr.detectChanges();
    this.mortgageApplicationService.saveLoanApplication(createApplicationRequest).subscribe(
      response => {
        if (response.authToken && response.refreshToken) {
          this._authService.saveAuthTokenDirectly(response.authToken, response.refreshToken);
          this.stopSpinner();
          this._navigationService.navigateToPath("apply", true, undefined, response.applicationId ? { appId: response.applicationId } : null);
        } else if (response.loginError) {
          this.stopSpinner();
          this.showError(new ErrorMessage(this.translocoService.translate('wizard.createAccount.errorLoggingIn'), response.loginError));
          this.cdr.detectChanges();
        } else {
          this.loginAndGotoApplyFlow(this.step.borrower.primaryEmail, <string>createApplicationRequest.password);
        }
      },
      error => {
        this.stopSpinner();
        this.showError({
          title: this.translocoService.translate('wizard.createAccount.errorCreatingAccount'),
          message: error.error && error.error.message ? error.error.message : this.translocoService.translate('wizard.createAccount.errorCreatingAccount')
        });
        this.cdr.detectChanges();
      }
    );
  }

  private createUserAccountAndMortgageWhenNotAutoConfirmed = () => {
    const createApplicationRequest: CreateBorrowerAccountMortgageRequest = {
      companyGuid: this._wizardFlowService.companyGuid,
      branchGuid: this._wizardFlowService.branchGuid,
      password: null,
      campaignId: this._wizardFlowService.campaignId,
      leadId: this._wizardFlowService.leadId,
      leadGuid: this._wizardFlowService.leadGuid,
      leadSourceOverride: this._wizardFlowService.leadSourceOverride || this._wizardFlowService.context.leadSource,
      mortgage: this.mortgageApplication,
      userGuid: this.mortgageApplication.userGuid,
      referralSource: this._wizardFlowService.referralSource || this._wizardFlowService.context.referralAgentId || undefined,
      agentTypeId: this._wizardFlowService.context.agentTypeId || undefined,
      flowGuid: this._wizardFlowConfigService.flowConfigs.guid,
      loanPurposeId: this._wizardFlowService.context.lead?.loanPurposeId || this._wizardFlowService.context.loanPurposeId,
      smsOptIn: this.optInForReceivingSms
    }
    if (createApplicationRequest.userGuid === 'unassigned') {
      createApplicationRequest.userGuid = undefined;
    }
    this.startSpinner();
    this.cdr.detectChanges();
    this.mortgageApplicationService.saveLoanApplication(createApplicationRequest).subscribe(
      response => {
        this._navigationService.navigateToCheckEmailNotice(this.step.borrower!.primaryEmail);
      },
      error => {
        if (this.isPasswordPolicyViolation(error)) {
          const passwordPolicyErrors = error.error.message.match(/\[.*?\]/g);
          this.passwordPolicyViolations = passwordPolicyErrors.map((e: string) => ({
            id: Utils.getUniqueId(),
            message: e.replace(/[[\]']+/g, '')
          }));
          this.cdr.detectChanges();
          return;
        }
        this.showError({
          title: this.translocoService.translate('wizard.createAccount.errorCreatingAccount'),
          message: error.error && error.error.message ? error.error.message : this.translocoService.translate('wizard.createAccount.errorCreatingAccount')
        });
        this.cdr.detectChanges();
      }
    ).add(() => {
      this.stopSpinner();
      this.cdr.detectChanges();
    });
  }

  private isPasswordPolicyViolation = (error: any): boolean => {
    return error.error && error.error.message && error.error.message.includes('[') && error.error.message.includes(']')
  }

  private showRequestPasswordDialog = (userName: string) => {
    const createApplicationRequest: CreateBorrowerAccountMortgageRequest = {
      companyGuid: this._wizardFlowService.companyGuid,
      branchGuid: this._wizardFlowService.branchGuid,
      password: this.borrowerInfo.password,
      campaignId: this._wizardFlowService.campaignId,
      leadId: this._wizardFlowService.leadId,
      leadGuid: this._wizardFlowService.leadGuid,
      leadSourceOverride: this._wizardFlowService.leadSourceOverride || this._wizardFlowService.context.leadSource,
      mortgage: this.mortgageApplication,
      userGuid: this.mortgageApplication.userGuid,
      referralSource: this._wizardFlowService.referralSource || this._wizardFlowService.context.referralAgentId || undefined,
      agentTypeId: this._wizardFlowService.context.agentTypeId || undefined,
      flowGuid: this._wizardFlowConfigService.flowConfigs.guid,
      loanPurposeId: this._wizardFlowService.context.lead?.loanPurposeId || this._wizardFlowService.context.loanPurposeId,
      smsOptIn: this.optInForReceivingSms
    }
    if (createApplicationRequest.userGuid === 'unassigned') {
      createApplicationRequest.userGuid = undefined;
    }
    const modal = this._modalService.open(ExistingUserActionSelectionDialogComponent, { centered: true, windowClass: 'requestPasswordModalClass' });
    modal.componentInstance.userName = userName;
    modal.componentInstance.createApplicationRequest = createApplicationRequest;
    modal.result.then((result: ExistingAccountLoginResult) => {
      if (result.userForgotPassword || result.applyingForANewLoan) {
        console.log("User forgot password or applying for new loan!");
        this.showSendCodeForResettingPasswordDialog(createApplicationRequest, result.applyingForANewLoan);
      } else if (result.authToken && result.refreshToken) {
        this._authService.saveAuthTokenDirectly(result.authToken, result.refreshToken);
        this._navigationService.navigateToPath("apply", true, undefined, result.appId ? { appId: result.appId } : null);
      } else if (result.authToken) {
        this.loginAndGotoApplyFlow(userName, <string>createApplicationRequest.password, result.appId);
      }
    }, err => { });
  }

  private showSendCodeForResettingPasswordDialog = (createApplicationRequest: CreateBorrowerAccountMortgageRequest,
    applyingForANewLoan: boolean) => {
    const modal = this._modalService.open(PasswordResetSendCodeDialogComponent, { centered: true, windowClass: 'requestPasswordModalClass' });
    modal.componentInstance.email = this.borrowerInfo.borrower.primaryEmail;
    modal.result.then((email: string) => {
      this.showResetPasswordDialog(email, createApplicationRequest, applyingForANewLoan);
    }, error => { });
  }

  private showResetPasswordDialog = (userName: string, createApplicationRequest: CreateBorrowerAccountMortgageRequest,
    applyingForANewLoan: boolean) => {
    const modal = this._modalService.open(ResetPasswordDialogComponent, { centered: true, windowClass: 'requestPasswordModalClass' });
    modal.componentInstance.applyingForANewLoan = applyingForANewLoan;
    modal.componentInstance.userName = userName;
    modal.componentInstance.createApplicationRequest = createApplicationRequest;
    modal.result.then((result: ResetPasswordResult) => {
      if (result.authToken && result.refreshToken) {
        this._authService.saveAuthTokenDirectly(result.authToken, result.refreshToken);
        this._navigationService.navigateToPath("apply", true, undefined, result.appId ? { appId: result.appId } : null);
      } else if (result.authToken) {
        this.loginAndGotoApplyFlow(userName, <string>result.newPassword, result.appId);
      }
    }, error => { });
  }

  private loginAndGotoApplyFlow = (userName: string, password: string, appId?: number) => {
    const loginRequest: AuthenticationRequest = {
      companyGuid: this._wizardFlowService.companyGuid,
      username: userName,
      password: password,
      rememberMe: false,
      scope: 'Borrower',
      usernameValidationOnly: false
    }
    this.startSpinner();
    this.cdr.detectChanges();
    this._authService.signIn(loginRequest).subscribe(response => {
      this.stopSpinner();
      if (response.loginStatus === LoginStatus.Error) {
        this.showError(new ErrorMessage(this.translocoService.translate('wizard.createAccount.errorLoggingIn'), response.errorMessage));
        this.cdr.detectChanges();
        return;
      }
      this._navigationService.navigateToPath("apply", true, undefined, (appId ? { appId: appId } : null));
    }, error => {
      this.stopSpinner();
      this.showError(new ErrorMessage(this.translocoService.translate('wizard.createAccount.errorLoggingIn'), error?.message));
      this.cdr.detectChanges();
    });
  }

  /** The lender's document, or null after telling the borrower it couldn't be loaded (never the default instead). */
  private loadLegalDocument = (load: () => Promise<string>): Promise<string | null> =>
    loadLegalDocumentOrReport(load, () => {
      this.showError(new ErrorMessage(
        this.translocoService.translate('legal.unavailableTitle'),
        this.translocoService.translate('legal.unavailable')));
      this.cdr.markForCheck(); // OnPush: the failure arrives after the click's change detection
    })

  private getPrivacyStatement = async (): Promise<string> =>
    this._legalDocuments.getHtml('privacyPolicy', this._wizardFlowService.companyGuid);

  private getTermsOfUse = async (): Promise<string> =>
    this._legalDocuments.getHtml('termsOfUse', this._wizardFlowService.companyGuid);
}
